import { ICultureClient } from "@multiweb/clients/culture";
import { ILanguage, ILanguageExtended } from "@multiweb/types/contracts";
import { ASServices } from "OW.Remoting/Basic/ServiceEndpoints";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { CancellationToken } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { getCancellationTokenAsRequestContext } from "../../../RequestContext";
import { BaseClient } from "../BaseClient";

interface ILanguageDTO {
    Primarni: boolean;
    Viditelny: boolean;
    Hierarchie: number;
    ID: number;
    Culture: string;
    Popis: string;
    Kod: string;
    /** Nastavení collation pro třídění */
    SortCollation: string;
    /** Nastavení collation pro filtrování */
    CompareCollation: string;
    /** Nastavení collation pro like */
    LikeCollation: string;
}

/**
 * @internal
 */
export class CultureClient extends BaseClient implements ICultureClient {
    private _languagesLoading: Promise<readonly ILanguageExtended[]>;

    public async setLanguage(language: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<ILanguage> {
        const dto = await (this._communicationManager.callPOSTRequest("SetLanguage", ASServices.cultures, null, language, undefined, getCancellationTokenAsRequestContext(cancellationToken)) as Promise<ILanguageDTO>);
        return convertLanguage(dto);
    }

    public getSupportedLanguages(cancellationToken: CancellationToken = CancellationToken.none): Promise<readonly ILanguageExtended[]> {
        // jazyky se za běhu aplikace nemohou měnit, takže je možné je kešovat
        if (this._languagesLoading === undefined)
            this._languagesLoading = (this._communicationManager.callGETRequest("SupportedLanguages", ASServices.cultures, undefined, undefined, getCancellationTokenAsRequestContext(cancellationToken)) as Promise<ILanguageDTO[]>)
                .then(languages => languages.map(convertLanguageExtended));
        return this._languagesLoading;
    }

    public async getCurrentLanguage(cancellationToken: CancellationToken = CancellationToken.none): Promise<ILanguage> {
        const dto = await (this._communicationManager.callGETRequest("CurrentLanguage", ASServices.cultures, undefined, undefined, getCancellationTokenAsRequestContext(cancellationToken)) as Promise<ILanguageDTO>);
        return convertLanguage(dto);
    }

    public async saveLanguageProfile(code: string, cancellationToken: CancellationToken = CancellationToken.none): Promise<boolean> {
        return (await OWManager.runStaticMethodWithParams({ className: "UserProfile", methodName: "SaveLanguage", params: [ "<string>" + code + "</string>" ] }, cancellationToken)).resultValue as boolean;
    }

    public async loadLanguageProfile(cancellationToken: CancellationToken = CancellationToken.none): Promise<string | null> {
        const result = await OWManager.runStaticMethodWithParams({ className: "UserProfile", methodName: "LoadLanguage" }, cancellationToken);
        const languageResult = (result.resultValue as string)?.match(/<string>(.*?)<\/string>/);
        return languageResult ? languageResult[1] : null;
    }
}

const convertLanguage = (dto: ILanguageDTO): ILanguage => ({
    code: dto.Kod,
    culture: dto.Culture,
});

const convertLanguageExtended = (dto: ILanguageDTO): ILanguageExtended => ({
    code: dto.Kod,
    culture: dto.Culture,
    isPrimary: dto.Primarni,
    description: dto.Popis,
});
